import { useRef, useState, useMemo } from 'react';
// @mui
import { Box, Card, IconButton, Typography } from '@mui/material';
// components
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import moment from 'moment';
import { capturePHEvents } from '../../../analytics/posthog.analytics';
import { PH_FEED_CREATE } from '../../../utils/posthog.eventNames';
import { FormProvider, RHFTextField } from '../../../fl-components/hook-form';
import { MultiFilePreview } from '../../../fl-components/upload';
import Iconify from '../../../fl-components/Iconify';
import PollFeed from './PollFeed';
import { POLL_DURATION } from '../../../utils/constants';
import MCQFeed from './MCQFeed';
import palette from '../../../theme/palette';
import { ConvertToBase64 } from '../../../helper/commonHelper';
import { uploadFile } from '../../../service/upload.file.service';
import { createFeed } from '../../../service/feed.service';
import { ADD_FEED } from '../../../redux/constants';

// ----------------------------------------------------------------------

export default function CreateFeed() {
  const selectedOrg = useSelector((state) => state.org.selectedOrg);
  const currentUser = useSelector((state) => state.auth.currentUser);
  const orgFeeds = useSelector((state) => state.feeds.orgFeeds);
  const dispatch = useDispatch();
  // Input refs
  const fileInputRef = useRef(null);
  const mediaInputRef = useRef(null);
  const mcqRef = useRef(null);
  // Display poll
  const [isDisplayPoll, setDisplayPoll] = useState(false);
  // Display MCQ
  const [isDisplayMCQ, setDisplayMCQ] = useState(false);
  // Image select state
  const [selectedImages, setSelectedImages] = useState([]);
  // Selected files
  const [selectedFile, setSelectedFile] = useState([]);
  // Notification
  const { enqueueSnackbar } = useSnackbar();
  // Loading button
  const [postLoading, setPostLoading] = useState(false);

  const handleAttach = () => {
    fileInputRef.current?.click();
  };

  const handleMedia = () => {
    mediaInputRef.current?.click();
  };

  // Remove media files
  const handleRemoveMedia = (file) => {
    const filteredItems = selectedImages?.filter((_file) => _file !== file);
    setSelectedImages(filteredItems);
  };

  const PostSchema = Yup.object().shape({
    message: Yup.string().max(512)
  });

  const PollSchema = Yup.object().shape({
    question: Yup.string().required('Please enter question'),
    option1: Yup.string().required('Please enter option'),
    option2: Yup.string().required('Please enter option'),
    option3: Yup.string(),
    option4: Yup.string(),
    day: Yup.number(),
    hour: Yup.number()
  });

  const defaultValues = useMemo(
    () => ({
      message: ''
    }),
    []
  );

  const pollDefaultValues = useMemo(
    () => ({
      question: '',
      option1: '',
      option2: '',
      option3: '',
      option4: '',
      day: '0',
      hour: '1'
    }),
    []
  );

  const mcqDefaultValues = useMemo(
    () => ({
      question: '',
      option1: '',
      option2: '',
      option3: '',
      option4: '',
      duration: POLL_DURATION[0].value
    }),
    []
  );

  const methods = useForm({
    resolver: yupResolver(isDisplayPoll || isDisplayMCQ ? PollSchema : PostSchema),
    defaultValues: {
      ...(isDisplayPoll && pollDefaultValues),
      ...(!isDisplayPoll && !isDisplayMCQ && defaultValues),
      ...(isDisplayMCQ && mcqDefaultValues)
    }
  });

  const { handleSubmit, reset, getValues, watch, setError } = methods;

  // Get the feed type
  const getFeedType = () => {
    if (selectedImages.filter((si) => si.type.split('/')[0] === 'image').length) {
      return 'photo';
    }

    if (selectedImages.filter((si) => si.type.split('/')[0] === 'video').length) {
      return 'video';
    }

    if (selectedFile.length) {
      return 'attachment';
    }

    if (isDisplayPoll) {
      return 'poll';
    }

    if (isDisplayMCQ) {
      return 'mcq';
    }

    return 'text';
  };

  const getConvertedObj = async (file) => {
    const base64 = await ConvertToBase64(file);
    const fileObj = {
      base64,
      fileType: file.type,
      fileName: file.name,
      uploadFor: 'post',
      userId: currentUser.id,
      orgId: selectedOrg?.id ?? selectedOrg?.orgId
    };

    return fileObj;
  };

  const uploadFunction = async (file) => {
    const fileObj = await getConvertedObj(file);
    const res = await uploadFile('', fileObj);
    if (res) {
      const obj = {
        name: res.fileName,
        url: res.fileURL,
        logId: res.id
      };
      return obj;
    }
    return null;
  };

  // Upload images in squense.
  const uploadMedia = async () => {
    const uploadedArray = [];
    if (selectedImages.length) {
      for (let i = 0; i < selectedImages.length; i += 1) {
        const image = selectedImages[i];
        uploadedArray.push(getConvertedObj(image));
      }
      const uploadToStorage = await Promise.all(
        uploadedArray.map((arr) => arr.then((res) => uploadFile('', res)))
      );

      return uploadToStorage.map((data) => ({
        name: data.fileName,
        url: data.fileURL,
        logId: data.id
      }));
    }

    return uploadedArray;
  };

  // Upload file
  const uploadAttachment = async () => {
    const attachment = [];
    if (selectedFile.length) {
      const obj = await uploadFunction(selectedFile[0]);
      attachment.push(obj);
    }

    return attachment;
  };

  const getEndTime = () => {
    let endTime = '';

    if (getValues('day') === undefined && getValues('hour') === undefined) {
      setError('day');
      setError('hour', { message: 'Please select hour' });
      return endTime;
    }

    const day = parseInt(getValues('day'), 10);
    const hour = parseInt(getValues('hour'), 10);

    if (day === 0 && hour === 0) {
      setError('hour', { message: 'Please select hour' });
      return endTime;
    }

    endTime =
      day > 0
        ? moment().add(day, 'day').hour(hour).second(0).toISOString()
        : moment().add(hour, 'hour').second(0).toISOString();

    return endTime;
  };

  const onSubmit = async () => {
    setPostLoading(true);
    const feedObj = {
      orgId: selectedOrg?.id ?? selectedOrg?.orgId,
      createdBy: currentUser.id
    };
    feedObj.type = getFeedType();
    if (getValues('message').trim() !== '') feedObj.content = getValues('message');

    if (feedObj.type === 'photo' || feedObj.type === 'video') {
      feedObj.medias = await uploadMedia();
    }

    if (feedObj.type === 'attachment') {
      feedObj.medias = await uploadAttachment();
    }

    if (feedObj.type === 'text' && getValues('message').trim() === '') {
      setError('message', {
        message: 'Please enter some message'
      });
      setPostLoading(false);
      return;
    }

    if (isDisplayPoll) {
      // Make api call for poll
      feedObj.content = getValues('question');
      const options = [{ value: getValues('option1') }, { value: getValues('option2') }];
      if (getValues('option3')) options.push({ value: getValues('option3') });
      if (getValues('option4')) options.push({ value: getValues('option4') });

      const endTime = getEndTime();
      if (endTime) {
        feedObj.pollOptions = options;
        // feedObj.startTime = moment().toISOString();
        feedObj.endTime = endTime;

        const feedRes = await createFeed(feedObj);
        if (feedRes) {
          reset();
          capturePHEvents(PH_FEED_CREATE, {
            feedId: feedRes?.id ?? feedRes?._id,
            feedType: feedRes.type
          });
          dispatch({
            type: ADD_FEED,
            payload: {
              feedObj: feedRes,
              addToOrg: orgFeeds !== null
            }
          });
          setDisplayPoll(false);
          setPostLoading(false);
        } else {
          setPostLoading(false);
        }
      } else setPostLoading(false);
      return;
    }

    if (isDisplayMCQ) {
      // Call children method to check is answer is selected
      const answerIndex = mcqRef.current.validateAnswer();
      if (answerIndex !== null) {
        feedObj.content = getValues('question');
        // If answer is selected then do api call
        const options = [
          { value: getValues('option1'), isAnswer: answerIndex === 0 },
          { value: getValues('option2'), isAnswer: answerIndex === 1 }
        ];

        if (getValues('option3')) {
          options.push({ value: getValues('option3'), isAnswer: answerIndex === 2 });
        }

        if (getValues('option4')) {
          options.push({ value: getValues('option4'), isAnswer: answerIndex === 3 });
        }

        feedObj.mcqOptions = options;
        feedObj.endTime = moment().add(1, 'hour').toISOString();

        const feedRes = await createFeed(feedObj);
        if (feedRes) {
          reset();
          capturePHEvents(PH_FEED_CREATE, {
            feedId: feedRes?.id ?? feedRes?._id,
            feedType: feedRes.type
          });
          dispatch({
            type: ADD_FEED,
            payload: {
              feedObj: feedRes,
              addToOrg: orgFeeds !== null
            }
          });
          setDisplayMCQ(false);
          setPostLoading(false);
        } else {
          setPostLoading(false);
        }
        return;
      }
      setPostLoading(false);
      return;
    }

    // Make api calls
    const feedRes = await createFeed(feedObj);
    if (feedRes) {
      capturePHEvents(PH_FEED_CREATE, {
        feedId: feedRes?.id ?? feedRes?._id,
        feedType: feedRes.type
      });
      dispatch({
        type: ADD_FEED,
        payload: {
          feedObj: feedRes,
          addToOrg: orgFeeds !== null
        }
      });
      reset();
      setSelectedFile([]);
      setSelectedImages([]);
      setPostLoading(false);
    } else {
      setPostLoading(false);
    }
  };

  /**
   * This function is used to add the media files to state.
   * @param {object} e input file object
   */
  const handleMediaFileChange = (e) => {
    if (selectedImages.length === 0) {
      setSelectedImages((prevState) => [
        ...prevState,
        Object.assign(e.target.files[0], {
          preview: URL.createObjectURL(e.target.files[0])
        })
      ]);
      return;
    }

    // Check number of file upload limit.
    // If image then max 5 image need to be upload.
    if (selectedImages.filter((si) => si.type.split('/')[0] === 'image').length === 5) {
      enqueueSnackbar('Only 5 images are allowed', {
        variant: 'error'
      });
      return;
    }

    // If video is there then only one video is allowed.
    if (selectedImages.filter((si) => si.type.split('/')[0] === 'video').length === 1) {
      enqueueSnackbar('Only 1 video is allowed', {
        variant: 'error'
      });
      return;
    }

    // Check for similar file type are only valid to select.
    // For example, if image is selected then only image should be select.
    // If video is selected then no image shouldmcqRef be select.
    if (
      selectedImages.filter((si) => si.type.split('/')[0] === e.target.files[0].type.split('/')[0])
        .length > 0
    ) {
      setSelectedImages((prevState) => [
        ...prevState,
        Object.assign(e.target.files[0], {
          preview: URL.createObjectURL(e.target.files[0])
        })
      ]);
    } else {
      enqueueSnackbar('This file has different type remove other to support this one.', {
        variant: 'error'
      });
    }
  };

  return (
    <Card sx={{ p: 3 }}>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        {isDisplayMCQ && <MCQFeed ref={mcqRef} />}
        {isDisplayPoll && <PollFeed />}
        {!isDisplayPoll && !isDisplayMCQ && (
          <>
            <RHFTextField
              name="message"
              multiline
              fullWidth
              rows={4}
              placeholder="Share what you are thinking here..."
              sx={{
                '& fieldset': {
                  borderWidth: `1px !important`,
                  borderColor: (theme) => `${theme.palette.grey[500_32]} !important`
                }
              }}
              inputProps={{ maxLength: 512 }}
            />
            <Typography textAlign="right" mt={1} color={palette.text.secondary}>{`${
              watch('message').length
            }/512`}</Typography>

            <MultiFilePreview
              files={selectedImages.length ? selectedImages : selectedFile}
              showPreview={selectedImages.length > 0}
              onRemove={
                selectedImages.length
                  ? (file) => handleRemoveMedia(file)
                  : () => setSelectedFile([])
              }
            />
          </>
        )}

        <Box
          sx={{
            mt: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex' }}>
            {/* Image/Video Icon */}
            <IconButton
              size="small"
              onClick={handleMedia}
              sx={{ mr: 1 }}
              disabled={isDisplayPoll || selectedFile.length > 0 || isDisplayMCQ}
            >
              <Iconify icon="ic:round-add-photo-alternate" width={24} height={24} />
            </IconButton>
            {/* Attachment Icon */}
            <IconButton
              size="small"
              onClick={handleAttach}
              sx={{ mr: 1 }}
              disabled={isDisplayPoll || selectedImages.length > 0 || isDisplayMCQ}
            >
              <Iconify icon="eva:attach-2-fill" width={24} height={24} />
            </IconButton>

            {/* Poll Icon */}
            <IconButton
              size="small"
              sx={{ mr: 1 }}
              disabled={isDisplayMCQ || selectedImages.length > 0 || selectedFile.length > 0}
              onClick={() => {
                setDisplayPoll(!isDisplayPoll);
                reset();
              }}
            >
              <Iconify icon="fa-solid:poll" width={24} height={24} />
            </IconButton>

            {/* Quiz Icon */}
            <IconButton
              size="small"
              disabled={isDisplayPoll || selectedImages.length > 0 || selectedFile.length > 0}
              onClick={() => {
                setDisplayMCQ(!isDisplayMCQ);
                reset();
              }}
            >
              <Iconify icon="fluent:quiz-new-20-filled" width={24} height={24} />
            </IconButton>
          </Box>
          <LoadingButton loading={postLoading} variant="contained" type="submit">
            Post
          </LoadingButton>
        </Box>
      </FormProvider>

      <input
        ref={fileInputRef}
        accept=".pdf"
        type="file"
        style={{ display: 'none' }}
        onChange={(e) => setSelectedFile([e.target.files[0]])}
      />
      <input
        ref={mediaInputRef}
        accept="image/png, image/jpeg, video/mp4, video/x-m4v, video/*"
        type="file"
        style={{ display: 'none' }}
        onChange={(e) => {
          handleMediaFileChange(e);
          e.target.value = '';
        }}
      />
    </Card>
  );
}
